<template>
  <div class="page-pre-projects-import">
    <div class="page-pre-projects-import__content">
      <!-- Step 1: the CSV. One file at a time — picking another replaces it
           and voids any preview made on the previous one. -->
      <FileUpload
        v-if="!file"
        icon="upload"
        :title="$t('page.admin-pre-projects-import.upload-title')"
        allowed-files=".csv,text/csv"
        @add-files="onFilesAdded"
      />
      <div v-else class="page-pre-projects-import__file">
        <BaseIcon name="file-text" class="page-pre-projects-import__file-ico" />
        <span class="page-pre-projects-import__file-name">{{ file.name }}</span>
        <span class="page-pre-projects-import__file-size">{{ fileSize }}</span>
        <BaseRoundButton
          :size="32"
          is-transparent
          icon="x"
          :tooltip-text="$t('page.admin-pre-projects-import.remove-file')"
          :disabled="!!pending"
          @click.native="clearFile"
        />
      </div>

      <!-- Step 2: preview (dry run), then import. The import only unlocks once
           the current file has been previewed — the numbers are what the
           admin is agreeing to. -->
      <div class="page-pre-projects-import__actions">
        <BaseButton
          isOutlined
          hasIconLeft
          :disabled="!file || !!pending"
          :isLoading="pending === 'preview'"
          @click.native="runPreview"
        >
          <BaseIcon :name="pending === 'preview' ? 'loader' : 'eye'" />
          <span>
            {{
              pending === 'preview'
                ? $t('page.admin-pre-projects-import.preview-pending')
                : $t('page.admin-pre-projects-import.preview-action')
            }}
          </span>
        </BaseButton>
        <BaseButton hasIconLeft :disabled="!canImport" :isLoading="pending === 'import'" @click.native="runImport">
          <BaseIcon :name="pending === 'import' ? 'loader' : 'upload'" />
          <span>
            {{
              pending === 'import'
                ? $t('page.admin-pre-projects-import.import-pending')
                : $t('page.admin-pre-projects-import.import-action')
            }}
          </span>
        </BaseButton>
        <span class="page-pre-projects-import__note">{{ $t('page.admin-pre-projects-import.full-file-note') }}</span>
      </div>

      <!-- Step 3: what the API reported — the preview, or the real import once done. -->
      <section v-if="report" class="page-pre-projects-import__report">
        <div class="page-pre-projects-import__report-head">
          <h2 class="page-pre-projects-import__heading">
            {{
              report.dryRun
                ? $t('page.admin-pre-projects-import.preview-heading')
                : $t('page.admin-pre-projects-import.result-heading')
            }}
          </h2>
          <BaseTag v-if="report.dryRun" tone="info" preset="soft" icon="eye">
            {{ $t('page.admin-pre-projects-import.preview-heading') }}
          </BaseTag>
          <BaseTag v-else tone="success" preset="soft" icon="check">
            {{ $t('page.admin-pre-projects-import.import-success') }}
          </BaseTag>
        </div>
        <p v-if="report.dryRun" class="page-pre-projects-import__hint">
          {{ $t('page.admin-pre-projects-import.preview-hint') }}
        </p>

        <!-- The important numbers: what the file covers and what changes. -->
        <div class="page-pre-projects-import__stats">
          <div
            v-for="stat in stats"
            :key="stat.key"
            class="page-pre-projects-import__stat"
            :class="[`is-${stat.tone}`, { 'is-zero': stat.value === 0 }]"
          >
            <span class="page-pre-projects-import__stat-value number-monospace">
              {{ formatLocalizedNumber(stat.value) }}
            </span>
            <span class="page-pre-projects-import__stat-label">
              {{ $t(`page.admin-pre-projects-import.stats.${stat.key}`) }}
            </span>
          </div>
        </div>

        <BaseInfoBanner
          v-if="report.errorCount > 0"
          class="page-pre-projects-import__banner"
          tone="warning"
          preset="soft"
          :title="$tc('page.admin-pre-projects-import.stats.errors', report.errorCount)"
          :description="$t('page.admin-pre-projects-import.errors-note')"
        >
          <template #icon>
            <BaseIcon name="alert-triangle" />
          </template>
        </BaseInfoBanner>

        <!-- Deleted pre-projects (full-file mode removes what the CSV no longer lists). -->
        <div v-if="deletedPreProjects.length" class="page-pre-projects-import__list">
          <h3 class="page-pre-projects-import__subheading">
            {{ $t('page.admin-pre-projects-import.deleted-heading') }}
            <span class="page-pre-projects-import__count">{{ formatLocalizedNumber(deletedPreProjects.length) }}</span>
          </h3>
          <ul class="page-pre-projects-import__deleted">
            <li v-for="(item, index) in visibleDeleted" :key="`deleted-${index}`">{{ deletedLabel(item) }}</li>
          </ul>
          <BaseButton
            v-if="deletedPreProjects.length > LIST_PREVIEW_SIZE"
            isSmall
            isTransparent
            isSquare
            @click.native="toggleList('deleted')"
          >
            {{ toggleLabel('deleted', deletedPreProjects.length) }}
          </BaseButton>
        </div>

        <!-- Line-level errors and warnings, capped until "show all". -->
        <div v-for="kind in ['errors', 'warnings']" :key="kind" class="page-pre-projects-import__list">
          <h3 class="page-pre-projects-import__subheading">
            {{ $t(`page.admin-pre-projects-import.${kind}-heading`) }}
            <span class="page-pre-projects-import__count">{{ formatLocalizedNumber(issues(kind).length) }}</span>
          </h3>
          <p v-if="!issues(kind).length" class="page-pre-projects-import__empty">
            {{ $t('page.admin-pre-projects-import.none') }}
          </p>
          <table v-else class="page-pre-projects-import__table" :class="`is-${kind}`">
            <thead>
              <tr>
                <th class="page-pre-projects-import__table-line">{{ $t('page.admin-pre-projects-import.line') }}</th>
                <th>{{ $t('page.admin-pre-projects-import.message') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(issue, index) in visibleIssues(kind)" :key="`${kind}-${issue.line}-${index}`">
                <td class="page-pre-projects-import__table-line number-monospace">{{ issue.line }}</td>
                <td>{{ issue.message }}</td>
              </tr>
            </tbody>
          </table>
          <BaseButton
            v-if="issues(kind).length > LIST_PREVIEW_SIZE"
            isSmall
            isTransparent
            isSquare
            @click.native="toggleList(kind)"
          >
            {{ toggleLabel(kind, issues(kind).length) }}
          </BaseButton>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseInfoBanner from '@/components/base/BaseInfoBanner.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import FileUpload from '@/components/form/FileUpload.vue';
import { preProjectsService } from '@/services/pre-projects.service';
import { formatLocalizedNumber } from '@/util/number-util';

// Rows shown per list before "show all".
const LIST_PREVIEW_SIZE = 25;

// The stats to surface from the import result, in display order, with the
// tone their tile takes when non-zero.
const STATS = [
  { key: 'total', field: 'totalCount', tone: 'neutral' },
  { key: 'created', field: 'created', tone: 'success' },
  { key: 'updated', field: 'updated', tone: 'info' },
  { key: 'unchanged', field: 'unchanged', tone: 'neutral' },
  { key: 'deleted', field: 'deleted', tone: 'danger' },
  { key: 'errors', field: 'errorCount', tone: 'danger' },
  { key: 'warnings', field: 'warningCount', tone: 'warning' },
];

/**
 * System-admin import of the global pre-project catalog from a CSV, through
 * POST /pre-projects/import?fullFile=true. Two steps: a dry run (dryRun=true)
 * previews the counts, the deleted pre-projects and the line errors without
 * writing anything; the real import then runs on the same file.
 */
export default {
  name: 'PageAdminPreProjectsImport',
  components: {
    BaseButton,
    BaseIcon,
    BaseInfoBanner,
    BaseRoundButton,
    BaseTag,
    FileUpload,
  },
  data() {
    return {
      LIST_PREVIEW_SIZE,
      file: null,
      // 'preview' | 'import' while a call is in flight, else null.
      pending: null,
      // The last API result shown (dry run or real import).
      report: null,
      // The file the current preview was made on — the import only unlocks
      // while the picked file is still that one.
      previewedFile: null,
      // Which lists are fully expanded.
      expanded: { deleted: false, errors: false, warnings: false },
    };
  },
  computed: {
    fileSize() {
      if (!this.file) return '';
      const kb = this.file.size / 1024;
      return kb < 1024 ? `${formatLocalizedNumber(Math.round(kb))} KB` : `${(kb / 1024).toFixed(1)} MB`;
    },
    // A preview exists for exactly this file, and nothing is running.
    canImport() {
      return !!this.file && this.previewedFile === this.file && !!this.report && this.report.dryRun && !this.pending;
    },
    stats() {
      return STATS.map((stat) => ({
        key: stat.key,
        value: Number(this.report[stat.field]) || 0,
        tone: stat.tone,
      }));
    },
    deletedPreProjects() {
      return (this.report && this.report.deletedPreProjects) || [];
    },
    visibleDeleted() {
      return this.expanded.deleted ? this.deletedPreProjects : this.deletedPreProjects.slice(0, LIST_PREVIEW_SIZE);
    },
  },
  methods: {
    formatLocalizedNumber,
    onFilesAdded(files) {
      const file = Array.from(files || []).find(Boolean);
      if (!file) return;
      this.file = file;
      // A new file voids what was previewed before.
      this.report = null;
      this.previewedFile = null;
      this.expanded = { deleted: false, errors: false, warnings: false };
    },
    clearFile() {
      if (this.pending) return;
      this.file = null;
      this.report = null;
      this.previewedFile = null;
    },
    runPreview() {
      if (!this.file || this.pending) return;
      this.pending = 'preview';
      const file = this.file;
      return preProjectsService.importCsv(file, { fullFile: true, dryRun: true }).then(
        (result) => {
          this.pending = null;
          this.report = { ...result, dryRun: true };
          this.previewedFile = file;
          this.expanded = { deleted: false, errors: false, warnings: false };
        },
        (error) => {
          this.pending = null;
          this.$toast.error(this.$t('page.admin-pre-projects-import.preview-failed'));
          this.$toast.error(error);
        },
      );
    },
    runImport() {
      if (!this.canImport) {
        if (this.file && this.previewedFile !== this.file) {
          this.$toast.error(this.$t('page.admin-pre-projects-import.file-changed'));
        }
        return;
      }
      this.pending = 'import';
      return preProjectsService.importCsv(this.file, { fullFile: true, dryRun: false }).then(
        (result) => {
          this.pending = null;
          this.report = { ...result, dryRun: false };
          // The file is now imported — a second click must go through a fresh preview.
          this.previewedFile = null;
          this.expanded = { deleted: false, errors: false, warnings: false };
          this.$toast.success(this.$t('page.admin-pre-projects-import.import-success'));
        },
        (error) => {
          this.pending = null;
          this.$toast.error(this.$t('page.admin-pre-projects-import.import-failed'));
          this.$toast.error(error);
        },
      );
    },
    issues(kind) {
      return (this.report && this.report[kind]) || [];
    },
    visibleIssues(kind) {
      const list = this.issues(kind);
      return this.expanded[kind] ? list : list.slice(0, LIST_PREVIEW_SIZE);
    },
    toggleList(kind) {
      this.expanded = { ...this.expanded, [kind]: !this.expanded[kind] };
    },
    toggleLabel(kind, total) {
      return this.expanded[kind]
        ? this.$t('page.admin-pre-projects-import.show-less')
        : this.$t('page.admin-pre-projects-import.show-all', { count: formatLocalizedNumber(total) });
    },
    // The API lists deleted pre-projects either as objects (title, id) or ids.
    deletedLabel(item) {
      if (!item) return '';
      if (typeof item === 'string') return item;
      return item.title || item.projectNumber || item.id || JSON.stringify(item);
    },
  },
};
</script>

<style lang="scss">
.page-pre-projects-import {
  // 32px matches every other admin page.
  &__content {
    padding: 32px;
    max-width: 1100px;
  }

  // The picked file, in place of the drop zone.
  &__file {
    display: flex;
    align-items: center;
    gap: $space-2;
    padding: $space-3 $space-md;
    background: $color-white;
    border: 1px solid $color-border;
    border-radius: $border-radius-root;
  }

  &__file-ico {
    width: 18px;
    height: 18px;
    flex: none;
    color: $color-slate-500;
  }

  &__file-name {
    flex: 1;
    min-width: 0;
    font-weight: 600;
    color: $color-normal;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__file-size {
    flex: none;
    font-size: $font-size-xsmall;
    color: $color-slate-500;
  }

  &__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: $space-3;
    margin: $space-md 0;
  }

  &__note {
    font-size: $font-size-xsmall;
    color: $color-slate-500;
  }

  &__report {
    background: $color-white;
    border: 1px solid $color-border;
    border-radius: $border-radius-root;
    padding: $space-md;
  }

  &__report-head {
    display: flex;
    align-items: center;
    gap: $space-3;
  }

  &__heading {
    margin: 0;
    font-size: $font-size-bigger;
    font-weight: 700;
    color: $color-normal;
  }

  &__hint {
    margin: $space-1 0 0;
    font-size: $font-size-small;
    color: $color-slate-500;
  }

  // The stat tiles: value over label, toned by what the number means.
  &__stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
    gap: $space-3;
    margin: $space-md 0;
  }

  &__stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: $space-3;
    border-radius: $border-radius-root;
    background: $color-slate-50;
    border-left: 3px solid $color-slate-300;

    &.is-success {
      border-left-color: $color-semantic-success;
    }
    &.is-info {
      border-left-color: $color-semantic-info;
    }
    &.is-warning {
      border-left-color: $color-semantic-warning;
    }
    &.is-danger {
      border-left-color: $color-semantic-error;
    }
    // A zero is good news for the risky counts — mute the accent.
    &.is-zero {
      border-left-color: $color-slate-300;
    }
  }

  &__stat-value {
    font-size: 22px;
    font-weight: 700;
    color: $color-normal;
  }

  &__stat-label {
    font-size: $font-size-xsmall;
    color: $color-slate-500;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  &__banner {
    margin-bottom: $space-md;
  }

  &__list {
    margin-top: $space-md;
  }

  &__subheading {
    display: flex;
    align-items: center;
    gap: $space-2;
    margin: 0 0 $space-2;
    font-size: $font-size-small;
    font-weight: 700;
    color: $color-normal;
  }

  &__count {
    font-weight: 600;
    font-size: $font-size-xsmall;
    color: $color-slate-500;
    background: $color-slate-100;
    border-radius: 10px;
    padding: 1px 8px;
  }

  &__empty {
    margin: 0;
    font-size: $font-size-small;
    color: $color-slate-500;
  }

  &__deleted {
    margin: 0 0 $space-2;
    padding-left: $space-md;
    font-size: $font-size-small;
    color: $color-normal;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: $font-size-small;
    margin-bottom: $space-2;

    th {
      text-align: left;
      font-size: $font-size-xsmall;
      font-weight: 700;
      color: $color-slate-500;
      text-transform: uppercase;
      padding: $space-1 $space-2;
      border-bottom: 1px solid $color-border;
    }

    td {
      padding: $space-1 $space-2;
      border-bottom: 1px solid $color-slate-100;
      color: $color-normal;
      vertical-align: top;
    }

    &.is-errors td:first-child {
      color: $color-semantic-error;
    }
    &.is-warnings td:first-child {
      color: $color-semantic-warning;
    }
  }

  &__table-line {
    width: 90px;
    white-space: nowrap;
  }
}
</style>
